import React, { useState, useEffect } from 'react';
import {
  Container,
  Form,
  Button,
  Card,
  Alert as BootstrapAlert,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { useFormats } from '../../context/formatContext';
import ColumnMapper from './ColumnMapper';
import ClubMapper from './ClubMapper';

const FormatEditor = ({ format, onClose, onSuccess }) => {
  const { analyzeFile, createFormat, updateFormat, systemFields, loading } = useFormats();

  // Form state
  const [formatName, setFormatName] = useState('');
  const [hasUnitsRow, setHasUnitsRow] = useState(false);
  const [unitsRowIndex, setUnitsRowIndex] = useState(1);
  const [isPublished, setIsPublished] = useState(false);

  // File analysis state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Mapping state
  const [columnMappings, setColumnMappings] = useState([]);
  const [clubMappings, setClubMappings] = useState([]);

  // UI state
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1=upload, 2=mappings, 3=settings

  // Initialize from existing format if editing
  useEffect(() => {
    if (format) {
      setFormatName(format.name);
      setHasUnitsRow(format.hasUnitsRow);
      setUnitsRowIndex(format.unitsRowIndex);
      setIsPublished(format.isPublished);
      setColumnMappings(format.columnMappings || []);
      setClubMappings(format.clubMappings || []);
      setStep(2); // Skip file upload if editing
    }
  }, [format]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setError(null);
    }
  };

  const handleAnalyze = async () => {
    if (!uploadedFile) {
      setError('Please select a file first');
      return;
    }

    try {
      setAnalyzing(true);
      setError(null);
      const data = await analyzeFile(uploadedFile);
      setAnalysisData(data);
      setHasUnitsRow(data.hasUnitsRow);
      setUnitsRowIndex(data.unitsRowIndex || 1);

      // Initialize column mappings from analysis
      const initialMappings = data.headers.map(header => {
        // Skip Date field - it's extracted from filename
        if (/^date$/i.test(header.trim())) {
          return {
            originalName: header,
            systemField: '',
            sourceUnit: 'none',
            targetUnit: 'none',
            requiresConversion: false
          };
        }

        // Try to auto-match to system fields with better fuzzy matching
        const normalizedHeader = header.replace(/[\s_-]/g, '').toLowerCase();

        // First pass: Try exact match
        let matchedField = systemFields.find(f => {
          const normalizedField = f.name.replace(/[\s_-]/g, '').toLowerCase();
          return normalizedHeader === normalizedField;
        });

        // Second pass: Try substring match, but only if it's a substantial match
        // (avoid "Club" matching "ClubSpeed")
        if (!matchedField) {
          matchedField = systemFields.find(f => {
            const normalizedField = f.name.replace(/[\s_-]/g, '').toLowerCase();
            // Header contains field name - but field must be at least 50% of header length
            if (normalizedHeader.includes(normalizedField) && normalizedField.length >= normalizedHeader.length * 0.5) {
              return true;
            }
            // Field name contains header - but header must be at least 50% of field length
            if (normalizedField.includes(normalizedHeader) && normalizedHeader.length >= normalizedField.length * 0.5) {
              return true;
            }
            return false;
          });
        }

        const sourceUnit = data.detectedUnits[header] || 'none';
        const targetUnit = matchedField ? matchedField.defaultUnit : 'none';

        return {
          originalName: header,
          systemField: matchedField ? matchedField.name : '',
          sourceUnit,
          targetUnit,
          requiresConversion: sourceUnit !== 'none' && targetUnit !== 'none' && sourceUnit !== targetUnit
        };
      });

      setColumnMappings(initialMappings);

      // Initialize club mappings from analysis - ALL clubs must be mapped
      if (data.clubData && data.clubData.length > 0) {
        const initialClubMappings = data.clubData.map(club => ({
          originalName: club.originalName,
          standardName: club.suggestedName || ''
        }));
        setClubMappings(initialClubMappings);
      } else {
        // No clubs detected
        setClubMappings([]);
      }

      setStep(2); // Move to mappings step
    } catch (err) {
      setError(err.message || 'Error analyzing file');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleColumnMappingsChange = (updatedMappings) => {
    setColumnMappings(updatedMappings);
  };

  const handleClubMappingsChange = (updatedMappings) => {
    setClubMappings(updatedMappings);
  };

  const handleSave = async (publishNow = false) => {
    // Validation
    if (!formatName.trim()) {
      setError('Format name is required');
      return;
    }

    // Check that required fields are mapped
    const carryMapped = columnMappings.some(m => m.systemField === 'Carry');
    const clubMapped = columnMappings.some(m => m.systemField === 'Club');

    if (!carryMapped || !clubMapped) {
      setError('At minimum, Carry and Club fields must be mapped');
      return;
    }

    // Check that ALL clubs are mapped
    const unmappedClubs = clubMappings.filter(m => !m.standardName || m.standardName === '');
    if (unmappedClubs.length > 0) {
      setError(`All clubs must be mapped. ${unmappedClubs.length} club(s) still unmapped: ${unmappedClubs.map(c => c.originalName).join(', ')}`);
      return;
    }

    const formatData = {
      name: formatName,
      columnMappings: columnMappings.filter(m => m.systemField !== ''),
      clubMappings: clubMappings.filter(m => m.standardName !== ''),
      hasUnitsRow,
      unitsRowIndex,
      isPublished: publishNow || isPublished,
      sampleFileName: uploadedFile?.name || format?.sampleFileName
    };

    try {
      if (format) {
        // Update existing
        await updateFormat(format._id, formatData);
        onSuccess('Format updated successfully');
      } else {
        // Create new
        await createFormat(formatData);
        onSuccess('Format created successfully');
      }
    } catch (err) {
      setError(err.message || 'Error saving format');
    }
  };

  // Step 1: File Upload
  if (step === 1) {
    return (
      <Container>
        <style>
          {`
            .format-editor-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 2rem;
            }
            .btn-custom {
              background-color: #6366f1;
              color: #fff;
            }
            .btn-custom:hover {
              background-color: #5a5fd9;
              color: #fff;
            }
          `}
        </style>

        <div className="format-editor-header">
          <h2>{format ? 'Edit Format' : 'Create New Format'}</h2>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>

        {error && (
          <BootstrapAlert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </BootstrapAlert>
        )}

        <Card>
          <Card.Body>
            <h4>Step 1: Upload Sample CSV File</h4>
            <p className="text-muted">
              Upload a sample CSV file in the format you want to support. We'll analyze it to detect columns, units, and clubs.
            </p>

            <Form.Group className="mb-3">
              <Form.Label>Select CSV File</Form.Label>
              <Form.Control
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
              />
              {uploadedFile && (
                <Form.Text className="text-success">
                  Selected: {uploadedFile.name}
                </Form.Text>
              )}
            </Form.Group>

            <Button
              variant="primary"
              onClick={handleAnalyze}
              disabled={!uploadedFile || analyzing}
              className="btn-custom"
            >
              {analyzing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Analyzing...
                </>
              ) : (
                'Analyze File'
              )}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Step 2: Mappings
  if (step === 2) {
    return (
      <Container fluid>
        <style>
          {`
            .format-editor-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 2rem;
            }
            .btn-custom {
              background-color: #6366f1;
              color: #fff;
            }
            .btn-custom:hover {
              background-color: #5a5fd9;
              color: #fff;
            }
          `}
        </style>

        <div className="format-editor-header">
          <h2>{format ? 'Edit Format' : 'Create New Format'}</h2>
          <div>
            <Button variant="secondary" onClick={onClose} className="me-2">
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setStep(3)} className="btn-custom">
              Next: Settings
            </Button>
          </div>
        </div>

        {error && (
          <BootstrapAlert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </BootstrapAlert>
        )}

        {analysisData && (
          <BootstrapAlert variant="info">
            <strong>File Analyzed:</strong> {analysisData.fileName}
            {analysisData.hasUnitsRow && ' (Units row detected)'}
          </BootstrapAlert>
        )}

        <Row>
          <Col md={12}>
            <Card className="mb-4">
              <Card.Body>
                <h4>Step 2a: Column Mappings</h4>
                <p className="text-muted">
                  Map CSV columns to system fields and specify units for each field.
                </p>
                <ColumnMapper
                  columnMappings={columnMappings}
                  systemFields={systemFields}
                  detectedUnits={analysisData?.detectedUnits || {}}
                  exampleValues={analysisData?.exampleValues || {}}
                  onChange={handleColumnMappingsChange}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Card className="mb-4">
              <Card.Body>
                <h4>Step 2b: Club Mappings (Required)</h4>
                <p className="text-muted">
                  Map all club names found in the sample file to standard codes. All clubs must be mapped to ensure data consistency.
                </p>
                <ClubMapper
                  clubMappings={clubMappings}
                  detectedClubColumn={analysisData?.detectedClubColumn}
                  onChange={handleClubMappingsChange}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Step 3: Settings
  return (
    <Container>
      <style>
        {`
          .format-editor-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
          }
          .btn-custom {
            background-color: #6366f1;
            color: #fff;
          }
          .btn-custom:hover {
            background-color: #5a5fd9;
            color: #fff;
          }
        `}
      </style>

      <div className="format-editor-header">
        <h2>{format ? 'Edit Format' : 'Create New Format'}</h2>
        <div>
          <Button variant="secondary" onClick={onClose} className="me-2">
            Cancel
          </Button>
          <Button variant="outline-secondary" onClick={() => setStep(2)} className="me-2">
            Back
          </Button>
        </div>
      </div>

      {error && (
        <BootstrapAlert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </BootstrapAlert>
      )}

      <Card>
        <Card.Body>
          <h4>Step 3: Format Settings</h4>

          <Form.Group className="mb-3">
            <Form.Label>Format Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., TrackMan Indoor, FlightScope Mevo+"
              value={formatName}
              onChange={(e) => setFormatName(e.target.value)}
              required
            />
            <Form.Text className="text-muted">
              Must be unique. This name will appear in the upload format selector.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="CSV has units row (e.g., row 2 with [m], [mph], etc.)"
              checked={hasUnitsRow}
              onChange={(e) => setHasUnitsRow(e.target.checked)}
            />
          </Form.Group>

          {hasUnitsRow && (
            <Form.Group className="mb-3">
              <Form.Label>Units Row Index (0-based)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={unitsRowIndex}
                onChange={(e) => setUnitsRowIndex(parseInt(e.target.value))}
              />
              <Form.Text className="text-muted">
                Usually 1 (meaning row 2, after headers)
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Publish immediately (make available to all users)"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
            />
            <Form.Text className="text-muted">
              You can publish later from the format list
            </Form.Text>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={() => handleSave(false)}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSave(true)}
              disabled={loading}
              className="btn-custom"
            >
              {loading ? 'Saving...' : 'Save & Publish'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default FormatEditor;
